import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import Decimal from 'decimal.js';

import { PaymentPlanDto } from 'src/api/paymentPlanService/dto/PaymentPlanDto';
import { PaymentPlanStorageDto } from 'src/api/paymentPlanService/dto/PaymentPlanStorageDto';
import { InstalmentType } from 'src/shared/enum/InstalmentType';

import { Term } from '../types/booking-types';
import { UserChoices } from '../types/user-choices';

type PaymentPlanSlice = {
    userChoices: UserChoices;
    createdPaymentPlan: PaymentPlanStorageDto | null;
};

const initialState: PaymentPlanSlice = {
    userChoices: {
        numberOfStudents: 1,
        term: null,
        instalmentType: null,
        instalmentId: null,
    },
    createdPaymentPlan: null,
};

const paymentPlanSlice = createSlice({
    name: 'paymentPlan',
    initialState,
    reducers: {
        setNumberOfStudents: (state, action: PayloadAction<number>) => {
            state.userChoices.numberOfStudents = action.payload;
        },
        setTerm: (state, action: PayloadAction<Term>) => {
            state.userChoices.term = action.payload;
        },
        setInstalmentType: (state, action: PayloadAction<InstalmentType | null>) => {
            state.userChoices.instalmentType = action.payload;
        },
        setInstalmentId: (state, action: PayloadAction<string | null>) => {
            state.userChoices.instalmentId = action.payload;
        },
        setCreatedPaymentPlan: (state, action: PayloadAction<PaymentPlanDto>) => {
            state.createdPaymentPlan = new PaymentPlanStorageDto(action.payload);
        },
        setUserChoices: (state, action: PayloadAction<UserChoices>) => {
            state.userChoices = action.payload;
        },
        clearPaymentPlan: () => initialState,
    },
});

export const selectUserChoices = (state: { paymentPlan: PaymentPlanSlice }) =>
    state.paymentPlan.userChoices;
export const selectCreatedPaymentPlan = (state: { paymentPlan: PaymentPlanSlice }) => {
    const hasCreatedPaymentPlan = state.paymentPlan.createdPaymentPlan !== null;
    return hasCreatedPaymentPlan ? new PaymentPlanDto(state.paymentPlan.createdPaymentPlan) : null;
};
export const selectHasAdvancePayment = (state: { paymentPlan: PaymentPlanSlice }): boolean => {
    const { createdPaymentPlan } = state.paymentPlan;

    if (!createdPaymentPlan) return true;

    const advanceLicenceFee = createdPaymentPlan.paymentOptions[0];

    return new Decimal(advanceLicenceFee.price).gt(0);
};

export const selectPromotion = (state: { paymentPlan: PaymentPlanSlice }) =>
    state.paymentPlan.createdPaymentPlan?.promotion || null;
export const selectNumberOfStudents = createSelector;
export default paymentPlanSlice.reducer;

export const {
    setNumberOfStudents,
    setTerm,
    setInstalmentType,
    setInstalmentId,
    setCreatedPaymentPlan,
    setUserChoices,
    clearPaymentPlan,
} = paymentPlanSlice.actions;
